import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Mail, RefreshCw } from 'lucide-react';
import type { UserRole } from '@grid2/shared-types';
import { ApiError } from '../../api/client';
import { orgMembersApi, type MyPendingInvitation } from '../../api/orgMembers';
import { useSession } from '../../auth/SessionContext';
import { useOrgSwitch } from '../../hooks/useOrgSwitch';

const ROLE_LABELS: Record<UserRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  user: 'Member',
  'billing-admin': 'Billing admin',
};

function roleLabel(role: string): string {
  return role in ROLE_LABELS ? ROLE_LABELS[role as UserRole] : role;
}

/**
 * Translate an accept/decline failure into user-facing copy that says what
 * happened and what to do next (docs/app-design-standard.md §11). Raw upstream
 * messages are never shown. `dropRow` is true when the invitation can no
 * longer be acted on, so the row is removed instead of offering a dead control.
 */
function describeActionError(
  err: unknown,
  action: 'accept' | 'decline',
  orgName: string,
): { message: string; dropRow: boolean } {
  const status = err instanceof ApiError ? err.status : undefined;
  const code = err instanceof ApiError ? err.code : undefined;
  switch (status) {
    case 410:
      return {
        message: `Your invitation to ${orgName} has expired. Ask an organization admin to send a new one.`,
        dropRow: true,
      };
    case 409:
      // Only KNOWN terminal conflicts drop the row. Anything else (e.g.
      // INVITATION_ACCEPT_IN_PROGRESS, or an unrecognised code) is retryable,
      // so the row and its buttons stay.
      if (code === 'INVITATION_NOT_PENDING') {
        return {
          message: `Your invitation to ${orgName} is no longer pending — it may have been cancelled or already answered. Ask an organization admin if you still need access.`,
          dropRow: true,
        };
      }
      if (code === 'MEMBERSHIP_DISABLED') {
        return {
          message: `Your membership in ${orgName} was removed. Ask an organization admin to restore it.`,
          dropRow: true,
        };
      }
      return {
        message: `Your invitation to ${orgName} is still being processed. Try again in a moment.`,
        dropRow: false,
      };
    case 404:
      return {
        message: `Your invitation to ${orgName} no longer exists. Ask an organization admin to send a new one.`,
        dropRow: true,
      };
    case 403:
      return {
        message: `This invitation to ${orgName} was sent to a different email address than the one you signed in with. Sign in with the invited address, or ask an admin to invite this one.`,
        dropRow: false,
      };
    default:
      return {
        message: `We couldn't ${action} your invitation to ${orgName}. Check your connection and try again.`,
        dropRow: false,
      };
  }
}

type LoadState = 'loading' | 'loaded' | 'error';

export interface PendingInvitationsBannerProps {
  /** Injectable for tests — forwarded to `useOrgSwitch` (hard navigation otherwise). */
  navigate?: (path: string) => void;
}

/**
 * Post-login banner listing the signed-in user's OWN pending invitations
 * (org name + invited role) with Accept / Decline.
 *
 * Invitations were previously discoverable only through the email link; this
 * surfaces them in-app. The list is resolved server-side from the session's
 * OAuth-verified email (`GET /api/org-invitations/mine`).
 *
 * Accept = `POST /api/org-invitations/:id/accept` (creates the membership +
 * identity pointer), then the shared `useOrgSwitch` flow
 * (`POST /api/session/switch-org` → discard org-scoped cache → navigate), so
 * the user lands in the new org without logging out. Accepting an invite into
 * the org the user is already in just confirms and clears the row.
 *
 * States (docs/app-design-standard.md §8): while loading, and when the user
 * has no invitations, nothing renders — this is a supplementary, page-global
 * banner, so a placeholder would shift every page's layout. A failed load is
 * NOT shown as empty: a compact notice with Retry renders instead.
 * In-flight actions show visible progress and disable both buttons; decline
 * and same-org accept show an inline confirmation.
 */
export function PendingInvitationsBanner({ navigate }: PendingInvitationsBannerProps = {}) {
  const { orgId: currentOrgId } = useSession();
  const [invitations, setInvitations] = useState<MyPendingInvitation[]>([]);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [busy, setBusy] = useState<{ id: string; label: string } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  /** Set after a successful accept into ANOTHER org, until the switch lands. */
  const [joined, setJoined] = useState<{ orgId: string; orgName: string } | null>(null);
  const { switchTo, isSwitching, error: switchError } = useOrgSwitch(
    navigate ? { navigate } : {},
  );

  const load = useCallback(async (isCancelled: () => boolean = () => false) => {
    setLoadState('loading');
    try {
      const result = await orgMembersApi.listMine();
      if (isCancelled()) return;
      setInvitations(result.invitations);
      setLoadState('loaded');
    } catch {
      if (isCancelled()) return;
      setInvitations([]);
      setLoadState('error');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void load(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [load]);

  const removeRow = useCallback((invitationId: string) => {
    setInvitations((rows) => rows.filter((row) => row.invitationId !== invitationId));
  }, []);

  const handleAccept = useCallback(
    async (invitation: MyPendingInvitation) => {
      setBusy({ id: invitation.invitationId, label: `Joining ${invitation.orgName}…` });
      setActionError(null);
      setConfirmation(null);
      let joinedOrgId: string;
      try {
        const result = await orgMembersApi.accept(invitation.invitationId);
        joinedOrgId = result.orgId;
      } catch (err) {
        const { message, dropRow } = describeActionError(err, 'accept', invitation.orgName);
        setActionError(message);
        if (dropRow) removeRow(invitation.invitationId);
        setBusy(null);
        return;
      }
      removeRow(invitation.invitationId);
      if (joinedOrgId === currentOrgId) {
        setConfirmation(`You're a member of ${invitation.orgName}.`);
        setBusy(null);
        return;
      }
      setJoined({ orgId: joinedOrgId, orgName: invitation.orgName });
      setBusy({ id: invitation.invitationId, label: `Switching to ${invitation.orgName}…` });
      await switchTo(joinedOrgId);
      // Success navigates away; on failure useOrgSwitch surfaces `switchError`.
      setBusy(null);
    },
    [currentOrgId, removeRow, switchTo],
  );

  const handleDecline = useCallback(
    async (invitation: MyPendingInvitation) => {
      setBusy({ id: invitation.invitationId, label: `Declining invitation to ${invitation.orgName}…` });
      setActionError(null);
      setConfirmation(null);
      try {
        await orgMembersApi.decline(invitation.invitationId);
        removeRow(invitation.invitationId);
        setConfirmation(`Declined your invitation to ${invitation.orgName}.`);
      } catch (err) {
        const { message, dropRow } = describeActionError(err, 'decline', invitation.orgName);
        setActionError(message);
        if (dropRow) removeRow(invitation.invitationId);
      } finally {
        setBusy(null);
      }
    },
    [removeRow],
  );

  if (loadState === 'error') {
    return (
      <section
        aria-label="Pending invitations"
        className="border-b border-slate-200 bg-white px-4 py-2 sm:px-6"
      >
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p role="alert" className="text-sm text-slate-700">
            We couldn&apos;t check for organization invitations. Your current organization is unaffected.
          </p>
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#d64041]"
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Retry
          </button>
        </div>
      </section>
    );
  }

  // The membership exists but the session switch failed: offer an inline retry
  // (the hook's raw message is not shown; our copy says what to do next).
  const switchFailedAfterJoin = joined !== null && switchError !== null;
  const error = actionError ?? (switchFailedAfterJoin ? null : switchError);
  if (invitations.length === 0 && !error && !confirmation && !switchFailedAfterJoin) return null;

  const disabled = busy !== null || isSwitching;

  return (
    <section
      aria-label="Pending invitations"
      className="border-b border-slate-200 bg-white px-4 py-3 sm:px-6"
    >
      <div className="space-y-2">
        {error && (
          <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </p>
        )}
        {switchFailedAfterJoin && joined && (
          <div
            role="alert"
            className="flex flex-col gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
          >
            <p className="text-sm text-amber-900">
              You joined <span className="font-semibold">{joined.orgName}</span>, but we couldn&apos;t switch you to it.
              Try again, or pick it from the organization switcher.
            </p>
            <button
              type="button"
              onClick={() => void switchTo(joined.orgId)}
              disabled={isSwitching}
              className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-lg bg-[#d64041] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#b52f35] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#d64041] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Switch to {joined.orgName}
            </button>
          </div>
        )}
        {confirmation && (
          <p
            role="status"
            className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-900"
          >
            <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
            {confirmation}
          </p>
        )}
        {invitations.length > 0 && (
          <ul className="divide-y divide-slate-200 rounded-xl border border-[#d64041]/25 bg-[#d64041]/[0.04]">
            {invitations.map((invitation) => {
              const rowBusy = busy?.id === invitation.invitationId;
              return (
                <li
                  key={invitation.invitationId}
                  className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex min-w-0 items-start gap-3">
                    <span className="mt-0.5 rounded-lg bg-[#d64041]/10 p-1.5 text-[#b52f35]">
                      <Mail className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <p className="min-w-0 break-words text-sm text-slate-900">
                      You&apos;ve been invited to join{' '}
                      <span className="font-semibold">{invitation.orgName}</span> as{' '}
                      <span className="font-semibold">{roleLabel(invitation.role)}</span>.
                      {rowBusy && (
                        <span role="status" className="mt-0.5 block text-xs font-medium text-slate-600">
                          {busy?.label}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      onClick={() => void handleAccept(invitation)}
                      disabled={disabled}
                      aria-label={`Accept invitation to ${invitation.orgName}`}
                      className="inline-flex min-h-11 flex-1 items-center justify-center rounded-lg bg-[#d64041] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#b52f35] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#d64041] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none"
                    >
                      Accept
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDecline(invitation)}
                      disabled={disabled}
                      aria-label={`Decline invitation to ${invitation.orgName}`}
                      className="inline-flex min-h-11 flex-1 items-center justify-center rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#d64041] disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none"
                    >
                      Decline
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
